import { Directive, DestroyRef, ElementRef, NgZone, Renderer2, effect, inject, input, output } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { toSignal } from '@angular/core/rxjs-interop';

@Directive({
	selector: '[appInfiniteScroll]',
	standalone: true,
})
export class InfiniteScrollDirective {
	readonly threshold = input(200);
	readonly disabled = input(false);
	readonly scrolledToEnd = output<void>();

	private readonly matSelect = inject(MatSelect, { optional: true });
	private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly renderer = inject(Renderer2);
	private readonly destroyRef = inject(DestroyRef);
	private readonly ngZone = inject(NgZone);

	private unListen?: () => void;
	private rafPending = false;
	private rafId: number | null = null;
	private readonly isOpen = this.matSelect ? toSignal(this.matSelect.openedChange, { initialValue: false }) : null;

	constructor() {
		if (this.matSelect) {
			effect(
				() => {
					const open = this.isOpen!();
					const off = this.disabled();
					this.detach();
					if (!open || off) return;
					queueMicrotask(() => {
						const panelEl = this.matSelect!.panel?.nativeElement as HTMLElement | undefined;
						if (!panelEl) return;
						this.attach(panelEl);
					});
				},
				{ allowSignalWrites: false },
			);
		} else {
			effect(
				() => {
					const off = this.disabled();
					this.detach();
					if (off) return;
					const el = this.hostRef.nativeElement;
					this.attach(el);
				},
				{ allowSignalWrites: false },
			);
		}
		this.destroyRef.onDestroy(() => this.detach());
	}

	private attach(targetEl: HTMLElement): void {
		this.ngZone.runOutsideAngular(() => {
			this.unListen = this.renderer.listen(targetEl, 'scroll', () => {
				if (this.rafPending) return;
				this.rafPending = true;
				this.rafId = requestAnimationFrame(() => {
					this.rafPending = false;
					this.checkNearBottom(targetEl);
				});
			});
		});
	}

	private detach(): void {
		if (this.unListen) {
			this.unListen();
			this.unListen = undefined;
		}
		if (this.rafId != null) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
		this.rafPending = false;
	}

	private checkNearBottom(el: HTMLElement): void {
		const near = el.scrollTop + el.clientHeight >= el.scrollHeight - this.threshold();
		if (near) {
			this.ngZone.run(() => this.scrolledToEnd.emit());
		}
	}
}
